import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { Chip, Tooltip } from "@narmi/design_system";
import { Localized, useLocalization } from "@fluent/react";
import cc from "classcat";
import { onboarding } from "byzantine";
import { useTransition, animated } from "@react-spring/web";

import { useUserFeatures } from "../../../../components/contexts/UserFeaturesContext";
import { useSidebarContainer } from "../sidebar.hooks";
import { useOnboardingGuide } from "../../guides";

import styles from "./SidebarGuide.module.scss";

const SidebarGuide = () => {
  const { l10n } = useLocalization();
  const { user_guide_type: userGuideType } = useUserFeatures();
  const onboardingPromptsEnabled = userGuideType === "onboarding";
  const { fetchOnboardingPrompts, percentComplete, uncompletedPromptsCount } =
    onboarding.useOnboardingContainer("web");

  const { isCollapsed } = useSidebarContainer();
  const { shouldShowInSidebar, moveToDashboard } = useOnboardingGuide();

  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(500);

  useEffect(() => {
    if (onboardingPromptsEnabled) {
      fetchOnboardingPrompts();
    }
  }, [onboardingPromptsEnabled, fetchOnboardingPrompts]);

  const shouldShowGuide =
    onboardingPromptsEnabled &&
    uncompletedPromptsCount > 0 &&
    percentComplete < 100 &&
    shouldShowInSidebar;

  useLayoutEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight;
      setContentHeight(height);
    }
  }, [isCollapsed, shouldShowGuide]);

  const transitions = useTransition(shouldShowGuide, {
    from: {
      opacity: 0,
      maxHeight: 0,
      marginBottom: 0,
      transform: "translateY(-100%)",
    },
    enter: {
      opacity: 1,
      maxHeight: contentHeight,
      marginBottom: 32,
      transform: "translateY(0%)",
    },
    leave: {
      opacity: 0,
      maxHeight: 0,
      marginBottom: 0,
      transform: "translateY(0%)",
    },
    config: { tension: 180, friction: 26, clamp: true },
    immediate: !hasAnimatedIn,
    onRest: () => {
      if (shouldShowGuide && !hasAnimatedIn) {
        setHasAnimatedIn(true);
      }
    },
  });

  return transitions(
    (animationStyles, item) =>
      item && (
        <div ref={contentRef}>
          <animated.div
            style={{
              ...animationStyles,
              overflow: "visible",
            }}
          >
            {isCollapsed ? (
              <Tooltip
                text={l10n.getString("sidebar-guide-title")}
                side="right"
                wrapperDisplay="block"
              >
                <div className={styles["sidebar-guide-wrapper--collapsed"]}>
                  <button
                    className={styles["sidebar-guide-collapsed-button"]}
                    onClick={moveToDashboard}
                    type="button"
                    aria-label={l10n.getString("sidebar-guide-title")}
                  >
                    <span
                      className={cc([
                        "narmi-icon-check-circle fontColor--theme--primary",
                        styles["sidebar-guide-icon"],
                      ])}
                    />
                  </button>
                </div>
              </Tooltip>
            ) : (
              <div className={styles["sidebar-guide-wrapper"]}>
                <button
                  className={styles["sidebar-guide-card"]}
                  onClick={moveToDashboard}
                  type="button"
                  aria-label={l10n.getString("sidebar-guide-title")}
                >
                  <div className={styles["sidebar-guide-content"]}>
                    <div className={styles["sidebar-guide-header"]}>
                      <span
                        className={cc([
                          "narmi-icon-check-circle fontColor--theme--primary",
                          styles["sidebar-guide-icon"],
                        ])}
                      />
                      <Localized id="sidebar-guide-title">
                        <div className={styles["sidebar-guide-title"]}>
                          Continue account set up
                        </div>
                      </Localized>
                    </div>
                    <Chip
                      kind="success"
                      label={l10n.getString("sidebar-guide-progress", {
                        percentComplete: percentComplete || 0,
                      })}
                    />
                  </div>
                  <div className={styles["sidebar-guide-icon"]}>
                    <span className="narmi-icon-chevron-right fontSize--m" />
                  </div>
                </button>
              </div>
            )}
          </animated.div>
        </div>
      )
  );
};

export default memo(SidebarGuide);
